import { Link } from 'react-router-dom'
import { ModalShell } from '../../admin/modals/ModalShell'
import { useManagerRequestDetail } from './useDrilldown'

function formatRub(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${Math.round(n)}`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

export function RequestDetailModal({
  requestId,
  onClose,
}: {
  requestId: string
  onClose: () => void
}) {
  const { data, isLoading, isError } = useManagerRequestDetail(requestId)

  return (
    <ModalShell
      title={data ? `Заявка ${data.number ?? '—'}` : 'Заявка'}
      subtitle={
        data
          ? `${data.client.name} · Кат. ${data.client.category} · ${data.statusLabel}`
          : undefined
      }
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить заявку</p>}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Позиций" value={data.items.length} />
            <Stat label="С квотами" value={data.items.filter((i) => i.resultsCount > 0).length} />
            <Stat label="Комментарии" value={data.commentsCount} />
            <Stat label="Сумма КП" value={formatRub(data.kpSumRub)} />
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            <span>Создана: {formatDate(data.createdAt)}</span>
            {data.deadline && <span>Дедлайн: {formatDate(data.deadline)}</span>}
          </div>

          <div className="flex justify-end">
            <Link
              to={`/requests/${data.id}`}
              onClick={onClose}
              className="text-sm text-brand-600 hover:underline"
            >
              Открыть заявку полностью →
            </Link>
          </div>

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Позиции ({data.items.length})
            </div>
            {data.items.length === 0 ? (
              <p className="text-sm text-gray-400">Нет позиций</p>
            ) : (
              <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 max-h-[320px] overflow-y-auto">
                {data.items.map((i) => (
                  <div key={i.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <span className="font-mono text-xs text-gray-900 flex-1 truncate">
                      {i.partNumber}
                    </span>
                    <span className="text-xs text-gray-500 w-20 truncate">{i.brand ?? '—'}</span>
                    <span className="text-xs text-gray-500 w-16 tabular-nums text-right">
                      {i.quantity} шт
                    </span>
                    <span className="text-xs text-gray-500 w-24 truncate">{i.statusLabel}</span>
                    <span className="text-xs text-gray-700 w-20 text-right tabular-nums">
                      {i.bestPriceRub === null ? '—' : formatRub(i.bestPriceRub)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
